import React, { useContext } from 'react';
import qs from 'qs';
import memoize from 'memoize-async';
import cloneDeep from 'lodash/cloneDeep';
import { HeroChartContainer } from './HeroChartContainer';
import {
	PersonalizeOrder,
	isConfigItemVisible,
	getConfigIndex,
} from 'components/PersonalizeOrder';
import DownloadProgressBar from 'Data/DownloadData/DownloadProgressBar';
import { CategorySpendTreeChartContainer } from './CategorySpendTreeChartContainer';
import Explore from '../Explore';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { MetadataContext } from 'contexts/MetadataContext';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { ApiDataContext } from 'contexts/ApiDataContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { TAIL_SPEND } from 'utils/detailsChartDataConverter';
import { setLocalStorageItem, getLocalStorageItem } from 'utils/localStorage';
import { CHART_TYPES } from 'Details/DetailsChart/DetailsChart';
import {
	getWaterfallChartDataFromWheel,
	getSpendCategorizationChartDataFromWheel,
} from 'utils/apiRequests';
import {
	getCompaniesWithDefaults,
	getHeroChartParamsFromProps,
	getIsTailSpendFromBreadcrumbs,
} from 'Pmi/HeroChart/utils';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import RegionalSection from './RegionalSection';
import CommonVendors from './CommonVendors';
import ShareOfTotalSpendSection from './ShareOfTotalSpendSection';
import SpendOverTimeSection from './SpendOverTimeSection';
import SecondaryNav from 'SecondaryNav';
import {
	OrderedItemWrapper,
	ScrollContainer,
	SecondaryContent,
} from './styles';
import { INDIRECT, DIRECT } from './constants';
import { getPropsFromQueryStringConsumer } from './utils';
import { PmiContext } from './context';

const SELECTED_COMPANIES_LOCAL_STORAGE_NAME = 'pmi_selected_companies';

const memoizedApi = {
	getWaterfallChartDataFromWheel: memoize(getWaterfallChartDataFromWheel),
	getSpendCategorizationChartDataFromWheel: memoize(
		getSpendCategorizationChartDataFromWheel
	),
	async getWaiterFor(key) {
		this._waitMap = this._waitMap || {};

		if (this._waitMap[key]) {
			await this._waitMap[key];
		} else {
			let done = null;

			const waiter = {
				done() {
					if (done) {
						done();
					}
				},
			};
			this._waitMap[key] = new Promise(onDone => (done = onDone));

			return waiter;
		}
	},
};

export class Pmi extends React.Component {
	state = {
		hasExportError: false,
		isNotificationShown: false,
		triggerContentRefresh: false,
	};
	async componentDidMount() {
		this.preserveQueryStringStateIfNeeded();
	}

	componentDidUpdate() {
		this.preserveQueryStringStateIfNeeded();
		this.setCompaniesDefaultsIfNeeded();
	}

	setCompaniesDefaultsIfNeeded() {
		const {
			companies,
			setQueryStringStateAndReplace,
			companiesList,
		} = this.props;

		if (
			(!companies || companies.length === 0) &&
			companiesList &&
			companiesList.length > 0
		) {
			const companies =
				this.tryGetCompaniesFromLocalstorage() ||
				[...companiesList].slice(0, 4);

			setQueryStringStateAndReplace({ companies });
		} else if (companies && companies.length > 0) {
			setLocalStorageItem(SELECTED_COMPANIES_LOCAL_STORAGE_NAME, companies);
		}
	}

	tryGetCompaniesFromLocalstorage() {
		const { companiesList } = this.props;

		let companies = getLocalStorageItem(SELECTED_COMPANIES_LOCAL_STORAGE_NAME);

		companies = (companies || []).filter(company =>
			companiesList.includes(company)
		);

		return companies.length > 0 ? companies : null;
	}

	preserveQueryStringStateIfNeeded() {
		const { location, setQueryStringStateAndReplace } = this.props;

		// When no query string are defined,
		// preseve query string state and expose query strings in url
		if (location.search) {
			return;
		}

		const queryStringState = this.getQueryStringState();

		setQueryStringStateAndReplace(queryStringState);
	}

	getCleanupQueryStringState() {
		return {
			isTailSpend: null,
			hierarchy: null,
			spendType: null,
			harmonizedSupplierId: null,
			harmonizedSupplier: null,
			vendorName: null,
		};
	}

	getQueryStringState() {
		const {
			companiesList,
			unit,
			currency,
			dateFrom,
			dateTo,
			showNonSourceable,
			showAllCategories,
			showTailSpend,
			showAllSegments,
			waterfallShowAdjusted,
			waterfallShowL4Level,
			dimensions,
			tailSpend,
			customFilters,
			customFiltersValues,
			waterfallFilter,
			chartType,
			storedQueryId,
			companies,
			pmiBreadcrumbs,
		} = this.props;

		const cleanupState = this.getCleanupQueryStringState();

		const queryStringState = {
			...cleanupState,
			unit,
			currency,
			dateFrom,
			dateTo,
			showNonSourceable,
			showAllCategories,
			showTailSpend,
			showAllSegments,
			waterfallShowAdjusted,
			waterfallShowL4Level,
			dimensions,
			tailSpend,
			customFilters,
			customFiltersValues,
			waterfallFilter,
			chartType: chartType || CHART_TYPES.waterfall,
			storedQueryId,
			companies: getCompaniesWithDefaults(companies, companiesList),
			pmiBreadcrumbs,
		};

		return queryStringState;
	}

	showExplorePage = (overrides, transactionsTitle) => {
		let queryStringState = this.getQueryStringState();
		queryStringState = { ...queryStringState, ...overrides };

		const search = qs.stringify(queryStringState, {
			strictNullHandling: true,
			arrayFormat: 'repeat',
		});

		const { history, location } = this.props;
		const locationUrl = `/pmi/explore?${search}`;

		history.push(locationUrl, {
			prevPageUrl: location.pathname + location.search,
			transactionsTitle,
		});
	};

	getSecondaryContentQueryParams = () => {
		const { pmiBreadcrumbs } = this.props;
		let heroQueryParams = cloneDeep(getHeroChartParamsFromProps(this.props));
		let queryParams = {
			currency: heroQueryParams.currency,
			currencyType: 'fixed',
			filters: [...heroQueryParams.filters, 'sourceable'],
			filterValues: [
				...heroQueryParams.filterValues,
				!heroQueryParams.showNonSourceable,
			],
			hierarchy: heroQueryParams.hierarchy,
			dateFrom: heroQueryParams.dateFrom,
			dateTo: heroQueryParams.dateTo,
			harmonized: heroQueryParams.harmonized,
		};
		(pmiBreadcrumbs || []).forEach(breadcrumb => {
			if ([INDIRECT, DIRECT].includes(breadcrumb)) {
				queryParams.filters = [...queryParams.filters, 'spendType'];
				queryParams.filterValues = [
					...queryParams.filterValues,
					breadcrumb === DIRECT ? 0 : 1,
				];
			}
		});
		queryParams.hierarchy = (pmiBreadcrumbs || []).filter(
			h => ![INDIRECT, DIRECT, TAIL_SPEND].includes(h)
		);
		return queryParams;
	};

	handleNoDataStateChange = noData => {
		this.setState({ noData });
	};

	closeNotification = () => {
		this.setState({ isNotificationShown: false, hasExportError: false });
	};

	downloadFailed = () => {
		this.setState({ hasExportError: true });
	};

	clearDownloadError = () => {
		this.setState({ hasExportError: false });
	};

	showDownloadNotification = () => {
		this.setState({ isNotificationShown: true });

		setTimeout(() => {
			this.closeNotification();
		}, 5000);
	};

	refreshContent = () => {
		const { clearApiCache } = this.props;

		clearApiCache();
		this.setState({ triggerContentRefresh: true }, () => {
			this.setState({ triggerContentRefresh: false });
		});
	};

	render() {
		const {
			currencySymbol,
			pmiBreadcrumbs,
			location,
			companiesList,
			permissions,
			companies,
		} = this.props;

		const {
			hasExportError,
			isNotificationShown,
			noData,
			triggerContentRefresh,
		} = this.state;

		if (location.pathname && location.pathname.includes('/explore')) {
			return <Explore {...this.props} />;
		}

		const isTailSpend = getIsTailSpendFromBreadcrumbs(pmiBreadcrumbs);
		const secondaryContentQueryParams = this.getSecondaryContentQueryParams();
		let spendType = '';
		(pmiBreadcrumbs || []).forEach(breadcrumb => {
			if ([INDIRECT, DIRECT].includes(breadcrumb)) {
				spendType = breadcrumb.toUpperCase();
			}
		});

		return (
			<PmiContext.Provider value={{ refreshPmiContent: this.refreshContent }}>
				<SecondaryNav
					showPMINav
					disableNegativeSpendSelect
					companiesList={companiesList}
					queryStringState={this.getQueryStringState()}
				/>

				<ScrollContainer>
					<HeroChartContainer
						{...this.props}
						companiesList={companiesList}
						isTailSpend={isTailSpend}
						memoizedApi={memoizedApi}
						updateNoDataStateForAllCharts={this.handleNoDataStateChange}
						showExplorePage={this.showExplorePage}
					/>
					{!noData && (
						<SecondaryContent>
							<PersonalizeOrder>
								{configItems => (
									<React.Fragment>
										{isConfigItemVisible(configItems, 'RegionalSection') && (
											<OrderedItemWrapper
												id="RegionalSection"
												order={getConfigIndex(configItems, 'RegionalSection')}
											>
												{!triggerContentRefresh && (
													<RegionalSection
														currencySymbol={currencySymbol}
														queryParams={secondaryContentQueryParams}
														onDownloadError={this.downloadFailed}
														onDownloadSuccess={this.showDownloadNotification}
														onDownloadStart={this.clearDownloadError}
														showExplorePage={this.showExplorePage}
														pmiBreadcrumbs={pmiBreadcrumbs}
													/>
												)}
											</OrderedItemWrapper>
										)}
										{isConfigItemVisible(
											configItems,
											'ShareOfTotalSpendSection'
										) && (
											<OrderedItemWrapper
												id="ShareOfTotalSpendSection"
												order={getConfigIndex(
													configItems,
													'ShareOfTotalSpendSection'
												)}
											>
												{!triggerContentRefresh && (
													<ShareOfTotalSpendSection
														currencySymbol={currencySymbol}
														queryParams={secondaryContentQueryParams}
														spendType={spendType}
														onDownloadError={this.downloadFailed}
														onDownloadSuccess={this.showDownloadNotification}
														onDownloadStart={this.clearDownloadError}
														showExplorePage={this.showExplorePage}
														pmiBreadcrumbs={pmiBreadcrumbs}
														companies={companies}
													/>
												)}
											</OrderedItemWrapper>
										)}
										{isConfigItemVisible(configItems, 'CommonVendors') && (
											<OrderedItemWrapper
												id="CommonVendors"
												order={getConfigIndex(configItems, 'CommonVendors')}
											>
												{!triggerContentRefresh && (
													<CommonVendors
														currencySymbol={currencySymbol}
														queryParams={secondaryContentQueryParams}
														pmiBreadcrumbs={pmiBreadcrumbs}
														onDownloadError={this.downloadFailed}
														onDownloadSuccess={this.showDownloadNotification}
														onDownloadStart={this.clearDownloadError}
														showExplorePage={this.showExplorePage}
													/>
												)}
											</OrderedItemWrapper>
										)}
										{/* isConfigItemVisible is called inside of the component */}
										<CategorySpendTreeChartContainer
											{...this.props}
											companiesList={companiesList}
											isTailSpend={isTailSpend}
											memoizedApi={memoizedApi}
											configItems={configItems}
											onDownloadError={this.downloadFailed}
											onDownloadSuccess={this.showDownloadNotification}
											onDownloadStart={this.clearDownloadError}
											showExplorePage={this.showExplorePage}
											permissions={permissions}
										/>
										{isConfigItemVisible(
											configItems,
											'SpendOverTimeSection'
										) && (
											<OrderedItemWrapper
												id="SpendOverTimeSection"
												order={getConfigIndex(
													configItems,
													'SpendOverTimeSection'
												)}
											>
												{!triggerContentRefresh && (
													<SpendOverTimeSection
														currencySymbol={currencySymbol}
														queryParams={secondaryContentQueryParams}
														onDownloadError={this.downloadFailed}
														onDownloadSuccess={this.showDownloadNotification}
														onDownloadStart={this.clearDownloadError}
													/>
												)}
											</OrderedItemWrapper>
										)}
									</React.Fragment>
								)}
							</PersonalizeOrder>
						</SecondaryContent>
					)}
					{isNotificationShown && (
						<DownloadProgressBar
							error={hasExportError}
							successMessage="Generating file... You will receive a notification when the file has been generated"
							onClose={this.closeNotification}
						/>
					)}
				</ScrollContainer>
			</PmiContext.Provider>
		);
	}
}

export default React.memo(props => {
	const queryStringState = useContext(QueryStringContext);
	const { metadata } = useContext(MetadataContext);
	const { permissions } = useContext(PermissionsContext);
	const { clearApiCache, apiCacheTrigger } = useContext(ApiDataContext);
	const {
		hideDecimals,
		customFilters: customFiltersOptions,
		companiesList,
	} = useContext(SettingsContext);
	const { options } = useContext(SettingsOptionsContext);
	const { othersThreshold, currency: currencyList } = options;

	const { currency, dateRangeId } = queryStringState;
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);

	const convertedPropsFromQueryString = getPropsFromQueryStringConsumer(
		queryStringState
	);

	const dateRanges = options ? options.dateRanges : [];

	const selectedDateRange = dateRanges.find(
		item => item.id.toString() === dateRangeId
	);
	if (
		selectedDateRange &&
		(convertedPropsFromQueryString.dateFrom !== selectedDateRange.dateFrom ||
			convertedPropsFromQueryString.dateTo !== selectedDateRange.dateTo)
	) {
		const { setQueryStringStateAndPush } = queryStringState;
		const { dateFrom, dateTo } = selectedDateRange;

		setQueryStringStateAndPush({ dateFrom, dateTo });

		convertedPropsFromQueryString.dateFrom = dateFrom;
		convertedPropsFromQueryString.dateTo = dateTo;
	}

	return (
		<Pmi
			{...convertedPropsFromQueryString}
			{...props}
			currency={currency}
			currencySymbol={currencySymbol}
			settingsOptions={options}
			hideDecimals={hideDecimals}
			customFiltersOptions={customFiltersOptions.filter(o => o.isActive)}
			permissions={permissions}
			othersThreshold={othersThreshold}
			metadata={metadata}
			companiesList={companiesList}
			clearApiCache={clearApiCache}
			apiCacheTrigger={apiCacheTrigger}
		/>
	);
});
